import React from "react";
import { useTranslation } from "react-i18next";
import { Check, X } from "lucide-react";
import PriceDisplay from "./PriceDisplay";
import {
  getProductPriceForApp,
  hasAppPriceOverride,
  shouldShowAppPriceIcon,
} from "../utils/productAppPrice";
import {
  handlePlatformLogoError,
  resolvePlatformBadgeLogo,
} from "../utils/platformLogos";
import "./product.css";

function PlatformStatusIcon({ set }) {
  return (
    <span
      className={`product-app-price-panel__badge ${
        set
          ? "product-app-price-panel__badge--set"
          : "product-app-price-panel__badge--unset"
      }`}
      aria-hidden="true"
    >
      {set ? <Check size={10} strokeWidth={3} /> : <X size={10} strokeWidth={2.5} />}
    </span>
  );
}

export function ProductAppPriceRow({ product, app, showStatus = true, variant = "panel" }) {
  const { t, i18n } = useTranslation("translation");
  const isArabic = i18n.language === "ar";
  const hasOverride = hasAppPriceOverride(product, app.key);
  const isPopover = variant === "popover";
  const logo = isPopover ? null : resolvePlatformBadgeLogo(app);
  const displayName = isArabic && app.nameAr ? app.nameAr : app.name;
  const logoCornerClass =
    logo?.corners === "square"
      ? "product-app-price-panel__logo-wrap--square"
      : "product-app-price-panel__logo-wrap--rounded";
  const rowClass = isPopover
    ? "product-app-price-panel__row product-app-price-panel__row--popover"
    : "product-app-price-panel__row";

  return (
    <div className={rowClass}>
      {showStatus && !isPopover ? <PlatformStatusIcon set={hasOverride} /> : null}
      {!isPopover ? (
        logo?.src ? (
          <span
            className={`product-app-price-panel__logo-wrap ${logoCornerClass}`}
          >
            <img
              className="product-app-price-panel__logo"
              src={logo.src}
              alt=""
              onError={(event) => handlePlatformLogoError(event, app, { badge: true })}
            />
          </span>
        ) : (
          <span
            className="product-app-price-panel__logo-wrap product-app-price-panel__logo-wrap--rounded"
            aria-hidden="true"
          />
        )
      ) : null}
      <span className="product-app-price-panel__name">{displayName}</span>
      <span className="product-app-price-panel__value">
        {hasOverride ? (
          <PriceDisplay
            value={getProductPriceForApp(product, app.key)}
            iconSize={12}
            color="#030f1b"
          />
        ) : (
          <span className="product-app-price-panel__fallback">
            {t("defaultPriceFallback")}
          </span>
        )}
      </span>
    </div>
  );
}

export default function ProductAppPricePanel({ product, deliveryApps = [] }) {
  const { t } = useTranslation("translation");
  const showPlatforms = shouldShowAppPriceIcon(product, deliveryApps);

  return (
    <div className="product-app-price-panel">
      <div className="product-app-price-panel__card product-app-price-panel__card--default">
        <div className="product-app-price-panel__default-header">
          <span className="product-app-price-panel__title">{t("defaultPrice")}</span>
          <span className="product-app-price-panel__hint product-app-price-panel__hint--inline">
            {t("defaultPriceHint")}
          </span>
        </div>
        <div className="product-app-price-panel__default-amount">
          <PriceDisplay value={product?.price} iconSize={14} color="#030f1b" />
        </div>
      </div>

      {showPlatforms ? (
        <div className="product-app-price-panel__card product-app-price-panel__card--platforms">
          <div className="product-app-price-panel__platforms-header">
            <div className="product-app-price-panel__title">
              {t("productAvailabilityStatus")}
            </div>
            <p className="product-app-price-panel__hint">
              {t("productAvailabilityStatusHint")}
            </p>
          </div>
          <div className="product-app-price-panel__list" role="list">
            {deliveryApps.map((app) => (
              <ProductAppPriceRow key={app.key} product={product} app={app} />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
